import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { playerConnectionsService } from '@/services/playerConnectionsService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Users, UserCheck, X } from 'lucide-react';

interface PendingRequest {
  id: string;
  player_id: string;
  status: string;
  created_at: string;
  player_details?: {
    username?: string;
    first_name?: string;
    last_name?: string;
    profile_photo_url?: string;
  };
}

interface TeamPendingRequestsProps {
  teamId: number;
  currentUserId?: string;
  canManage: boolean;
}

const PLAYER_POSITIONS = [
  'Point Guard', 'Shooting Guard', 'Small Forward', 'Power Forward', 'Center',
  'Forward', 'Midfielder', 'Defender', 'Goalkeeper',
  'Pitcher', 'Catcher', 'First Base', 'Second Base', 'Third Base', 'Shortstop',
  'Left Field', 'Center Field', 'Right Field',
  'Other'
];

export const TeamPendingRequests = ({ teamId, currentUserId, canManage }: TeamPendingRequestsProps) => {
  const [pendingRequests, setPendingRequests] = useState<PendingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedPositions, setSelectedPositions] = useState<Record<string, string>>({});

  useEffect(() => {
    loadPendingRequests();
  }, [teamId]);

  const loadPendingRequests = async () => {
    try {
      setLoading(true);
      const requests = await playerConnectionsService.getPendingTeamConnections(teamId);
      console.log('Raw pending requests:', requests);
      
      // Get player details for each request
      const requestsWithDetails = await Promise.all(
        requests.map(async (request) => {
          console.log('Processing request for player_id:', request.player_id);
          
          try {
            // Try Supabase profiles first
            const { data: profile, error: profileError } = await supabase
              .from('profiles')
              .select('username, first_name, last_name, profile_photo_url')
              .eq('id', request.player_id)
              .maybeSingle();

            console.log('Supabase profile query result:', { profile, profileError });

            if (profile) {
              console.log('Found Supabase profile:', profile);
              return {
                ...request,
                player_details: profile
              };
            }

            // Try internal users - get basic info and photo separately
            const { data: internalUser, error: internalError } = await supabase
              .from('internal_users')
              .select('username, role, age_group')
              .eq('id', request.player_id)
              .maybeSingle();

            console.log('Internal user query result:', { internalUser, internalError });

            let profilePhotoUrl = null;

            // First, try to get profile photo from Supabase Storage
            try {
              // List all files in the user's storage folder
              const { data: filesList, error: listError } = await supabase.storage
                .from('profile-photos')
                .list(request.player_id);
              
              console.log('Storage files list:', filesList, 'Error:', listError);
              
              if (filesList && filesList.length > 0) {
                // Find the first image file (jpg, jpeg, png, etc.)
                const imageFile = filesList.find(file => 
                  file.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|webp)$/i)
                );
                
                if (imageFile) {
                  const { data: storageData } = supabase.storage
                    .from('profile-photos')
                    .getPublicUrl(`${request.player_id}/${imageFile.name}`);
                  
                  profilePhotoUrl = storageData?.publicUrl;
                  console.log('Found profile photo in storage:', profilePhotoUrl);
                }
              }
            } catch (error) {
              console.log('Error accessing storage for user:', request.player_id, error);
            }

            // If no photo in storage, fall back to player_photo_gallery
            if (!profilePhotoUrl) {
              const { data: photoData, error: photoError } = await supabase
                .from('player_photo_gallery')
                .select('photo_url')
                .eq('player_id', request.player_id)
                .eq('user_type', 'internal')
                .order('display_order', { ascending: true })
                .limit(1)
                .maybeSingle();

              console.log('Photo gallery query result:', { photoData, photoError });
              profilePhotoUrl = photoData?.photo_url || null;
            }

            const playerDetails = {
              username: internalUser?.username || 'Unknown Player',
              first_name: '',
              last_name: '',
              profile_photo_url: profilePhotoUrl
            };

            console.log('Final player details:', playerDetails);

            return {
              ...request,
              player_details: playerDetails
            };
          } catch (error) {
            console.error('Error fetching player details for player_id:', request.player_id, error);
            return {
              ...request,
              player_details: {
                username: 'Unknown Player',
                profile_photo_url: null
              }
            };
          }
        })
      );

      console.log('All requests with details:', requestsWithDetails);
      setPendingRequests(requestsWithDetails);
    } catch (error) {
      console.error('Error loading pending requests:', error);
      toast.error('Failed to load pending requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRequest = async (requestId: string, playerId: string) => {
    const position = selectedPositions[requestId];
    if (!position) {
      toast.error('Please select a position for the player');
      return;
    }

    try {
      console.log('Accepting request:', { requestId, playerId, position, teamId });
      
      // Update the connection status to confirmed
      const { error: updateError } = await supabase
        .from('player_team_connections')
        .update({ 
          confirmed: true, 
          confirmed_by: currentUserId,
          status: 'confirmed'
        })
        .eq('id', requestId);

      if (updateError) {
        console.error('Error updating connection:', updateError);
        throw updateError;
      }

      // Add to team_players table with position
      const { error: insertError } = await supabase
        .from('team_players')
        .insert({
          team_id: teamId,
          user_id: playerId,
          position: position
        });

      if (insertError) {
        console.error('Error adding to team_players:', insertError);
        throw insertError;
      }

      toast.success('Player request accepted and added to team');
      loadPendingRequests(); // Reload to update the list
    } catch (error) {
      console.error('Error accepting request:', error);
      toast.error('Failed to accept player request');
    }
  };

  const handleRejectRequest = async (requestId: string) => {
    try {
      // Simply delete the request
      const { error } = await supabase
        .from('player_team_connections')
        .delete()
        .eq('id', requestId);

      if (error) throw error;

      toast.success('Player request rejected');
      loadPendingRequests(); // Reload to update the list
    } catch (error) {
      console.error('Error rejecting request:', error);
      toast.error('Failed to reject player request');
    }
  };

  if (!canManage) return null;

  if (loading) {
    return (
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Users className="h-5 w-5" />
            Pending Player Requests
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-2">
            <div className="h-16 bg-gray-700 rounded"></div>
            <div className="h-16 bg-gray-700 rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (pendingRequests.length === 0) return null;

  return (
    <Card className="bg-gray-800 border-gray-700">
      <CardHeader>
        <CardTitle className="text-white flex items-center gap-2">
          <Users className="h-5 w-5" />
          Pending Player Requests ({pendingRequests.length})
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {pendingRequests.map((request) => (
          <div key={request.id} className="bg-gray-700 p-4 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {request.player_details?.profile_photo_url ? (
                  <img
                    src={request.player_details.profile_photo_url}
                    alt="Player"
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center">
                    <Users className="h-6 w-6 text-gray-400" />
                  </div>
                )}
                <div>
                  <h4 className="text-white font-semibold">
                    {request.player_details?.username || 
                     `${request.player_details?.first_name || ''} ${request.player_details?.last_name || ''}`.trim() ||
                     'Unknown Player'}
                  </h4>
                  <p className="text-gray-400 text-sm">
                    Requested {new Date(request.created_at).toLocaleDateString()}
                  </p>
                  <Badge variant="outline" className="text-yellow-400 border-yellow-400 mt-1">
                    {request.status}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Select
                  value={selectedPositions[request.id] || ''}
                  onValueChange={(value) => setSelectedPositions(prev => ({ ...prev, [request.id]: value }))}
                >
                  <SelectTrigger className="w-40 bg-gray-600 border-gray-500 text-white">
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-600 border-gray-500">
                    {PLAYER_POSITIONS.map((position) => (
                      <SelectItem key={position} value={position} className="text-white">
                        {position}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  onClick={() => handleAcceptRequest(request.id, request.player_id)}
                  disabled={!selectedPositions[request.id]}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <UserCheck className="h-4 w-4 mr-1" />
                  Accept
                </Button>

                <Button
                  onClick={() => handleRejectRequest(request.id)}
                  size="sm"
                  variant="destructive"
                >
                  <X className="h-4 w-4 mr-1" />
                  Reject
                </Button>
              </div>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};